import type { ContactRow } from '../../lib/capture/headmap'
import { BOUNDARY_PCT, meter } from '../../lib/capture/meter'

/** The bar: zones on the four words' boundaries, a marker at the reading. */
export function Meter({ kohm, label, estimated }: { kohm: unknown; label: string; estimated: boolean }) {
  const m = meter(kohm)
  const value = estimated ? (m.pct === null ? 'no estimate' : `estimated ${m.word}`) : `${m.value} · ${m.word}`
  return (
    <span className={`meter meter--${m.tier}`} role="img" aria-label={`${label}: ${value}`}>
      <i className="meter__zone meter__zone--good" style={{ left: 0, width: `${BOUNDARY_PCT.good}%` }} />
      <i className="meter__zone meter__zone--ok" style={{ left: `${BOUNDARY_PCT.good}%`, width: `${BOUNDARY_PCT.usable - BOUNDARY_PCT.good}%` }} />
      <i className="meter__zone meter__zone--weak" style={{ left: `${BOUNDARY_PCT.usable}%`, width: `${BOUNDARY_PCT.weak - BOUNDARY_PCT.usable}%` }} />
      <i className="meter__zone meter__zone--none" style={{ left: `${BOUNDARY_PCT.weak}%`, width: `${100 - BOUNDARY_PCT.weak}%` }} />
      {m.pct !== null && <i className={`meter__mark meter__mark--${m.tier}`} style={{ left: `${m.pct}%` }} aria-hidden="true" />}
    </span>
  )
}

/**
 * One row per contact: the dot, where it sits, the word, the meter and the
 * reading. On a headband whose quality is ESTIMATED from the signal the number
 * is not shown — it would read as a measurement — and the row says so.
 */
export function ContactRows({
  rows,
  kohmByChannel,
  estimated,
}: {
  rows: readonly ContactRow[]
  kohmByChannel: Readonly<Record<string, number | null | undefined>>
  estimated: boolean
}) {
  return (
    <ul className="contacts">
      {rows.map((r) => {
        const kohm = kohmByChannel[r.channel]
        const m = meter(kohm)
        return (
          <li
            key={r.channel}
            className={`contact contact--metered contact--${r.tier}${r.placed ? '' : ' contact--unplaced'}`}
            data-channel={r.channel}
            data-tier={r.tier}
          >
            <i className="contact__dot" aria-hidden="true" />
            <span className="contact__loc">
              {r.placed ? r.loc : `${r.channel} · not placed`} <span className="contact__ch">{r.channel}</span>
            </span>
            <span className="contact__word">{r.word}</span>
            <Meter kohm={kohm} label={r.placed ? r.loc : r.channel} estimated={estimated} />
            <span className="contact__value">{estimated ? (m.pct === null ? '—' : 'est.') : m.value}</span>
          </li>
        )
      })}
    </ul>
  )
}
